import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';

import { Alert, Box, Button, CircularProgress, Container, Stack, TextField, Typography } from '@mui/material';

import { getPurchases } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import PageHeader from '../../Common/Elements/PageHeader';

import CallCentreOpeningLine from './CallCentreOpeningLine';
import UserCard from './UserCard';

type Props = RouteComponentProps<{ phone?: string }>;

export default function FindByPhone({ match }: Props) {
  const { phone: phoneParam } = match.params;
  const history = useHistory();
  const tenant = useSelector((state: App.State) => state.tenant);

  const [phoneNumber, setPhoneNumber] = useState(phoneParam ? decodeURIComponent(phoneParam) : '');
  const [searchQuery, setSearchQuery] = useState(phoneParam ? decodeURIComponent(phoneParam) : '');

  // Auto-search when component mounts with phone parameter
  useEffect(() => {
    if (phoneParam?.trim()) {
      setSearchQuery(decodeURIComponent(phoneParam));
    }
  }, [phoneParam]);

  // Search for users by phone number
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ['users-by-phone', searchQuery, tenant.brand],
    queryFn: async ({ signal }) => {
      if (!searchQuery.trim()) {
        return [];
      }

      // Strip leading zero from phone number for API call
      const searchPhone = searchQuery.trim().replace(/^0/, '');

      const response = await UsersService.getUsers(
        {
          filter: searchPhone,
          limit: 50,
          offset: 0,
          brand: tenant.brand,
        },
        { signal },
      );

      return response;
    },
    enabled: !!searchQuery.trim(),
  });

  // Search for orders for each user
  const {
    data: usersWithOrders = [],
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery({
    queryKey: ['users-with-orders', users.map((u) => u.id_member), tenant.brand],
    queryFn: async () => {
      if (users.length === 0) {
        return [];
      }

      const userOrdersPromises = users.map(async (userSummary) => {
        try {
          const [ordersResponse, userDetails] = await Promise.all([
            getPurchases({
              page: 1,
              per_page: 50,
              brand: tenant.brand,
              customer_id: userSummary.id_member,
            }),
            UsersService.getUser(userSummary.id_member, { brand: tenant.brand }),
          ]);
          return {
            user: userDetails,
            orders: (ordersResponse.result || []).filter((order) => order.status !== 'abandoned'),
          };
        } catch (error) {
          console.error(`Error fetching data for user ${userSummary.id_member}:`, error);
          return {
            user: userSummary, // fallback to summary if full details fail
            orders: [],
          };
        }
      });

      const results = await Promise.all(userOrdersPromises);

      // Sort by most recently active users first
      return results.sort((a, b) => {
        const aLastLogin = a.user.lastLogin?.login_date;
        const bLastLogin = b.user.lastLogin?.login_date;

        // If both have last login dates, sort by most recent first
        if (aLastLogin && bLastLogin) {
          return new Date(bLastLogin).getTime() - new Date(aLastLogin).getTime();
        }

        // If only one has last login date, prioritize it
        if (aLastLogin && !bLastLogin) {
          return -1;
        }
        if (!aLastLogin && bLastLogin) {
          return 1;
        }

        // If neither has last login date, maintain original order
        return 0;
      });
    },
    enabled: users.length > 0,
  });

  const handleSearch = () => {
    if (phoneNumber.trim()) {
      setSearchQuery(phoneNumber.trim());
      // Update URL if phone number changed
      if (phoneNumber !== phoneParam) {
        history.push(`/find-by-phone/${encodeURIComponent(phoneNumber.trim())}`);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };

  const isLoading = usersLoading || ordersLoading;
  const hasError = usersError || ordersError;

  return (
    <Container maxWidth="xl">
      <PageHeader title="Find Orders by Phone Number" />

      <Box mt={4}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="Phone Number"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter phone number to search"
            variant="outlined"
            fullWidth
            sx={{ maxWidth: 400 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={!phoneNumber.trim() || isLoading}
          >
            Search
          </Button>
        </Stack>
      </Box>

      {hasError && (
        <Box mt={4}>
          <Alert severity="error">Error searching for users: {usersError?.message || ordersError?.message}</Alert>
        </Box>
      )}

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !hasError && searchQuery && (
        <Box mt={4}>
          {usersWithOrders.length === 0 ? (
            <Alert severity="info">No users found with phone number: {searchQuery}</Alert>
          ) : (
            <Box>
              <CallCentreOpeningLine usersWithOrders={usersWithOrders} />

              <Typography variant="h6" gutterBottom>
                Found {usersWithOrders.length} user{usersWithOrders.length !== 1 ? 's' : ''} with phone number:{' '}
                {searchQuery}
              </Typography>

              {usersWithOrders.map(({ user, orders }) => (
                <UserCard key={user.id_member} user={user} orders={orders} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}
